import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Pizza, Lock, CheckCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const valid = password.length >= 6 && password === confirm && token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, newPassword: password });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-4">
        <div className="pizza-card p-8 text-center max-w-sm" data-testid="reset-no-token">
          <AlertTriangle className="w-12 h-12 text-[#E23D28] mx-auto mb-3" />
          <h2 className="text-xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Invalid Link</h2>
          <p className="text-sm text-[#2A2A2A]/60 mb-4">This reset link is missing or invalid. Request a new one.</p>
          <Link to="/forgot-password" className="pizza-btn text-sm">Request New Link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-4">
      <div className="pizza-card p-6 sm:p-8 w-full max-w-sm" data-testid="reset-password-card">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#2E7D32] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center mx-auto mb-3 shadow-[3px_3px_0px_0px_rgba(42,42,42,1)]">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-3xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="reset-title">
            Set New Password
          </h1>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle className="w-12 h-12 text-[#2E7D32] mx-auto mb-3" />
            <h2 className="text-xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Password Reset!</h2>
            <p className="text-sm text-[#2A2A2A]/60 mb-4">Your password has been updated. You can now sign in.</p>
            <Link to="/login" className="pizza-btn text-sm" data-testid="reset-go-login">Sign In</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>New Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} required minLength={6}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters" className="pizza-input w-full pr-10" data-testid="reset-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Confirm Password</label>
              <input type={showPw ? 'text' : 'password'} required minLength={6}
                value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Type it again" className="pizza-input w-full" data-testid="reset-confirm" />
              {confirm && password !== confirm && (
                <p className="text-xs text-[#E23D28] mt-1">Passwords don't match</p>
              )}
            </div>
            {error && <p className="text-sm text-[#E23D28]" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={!valid || loading}
              className="pizza-btn w-full disabled:opacity-50" data-testid="reset-submit">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
